import { CheckboxGroup, Color } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import {
  Divider,
  Typography,
  makeStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField
} from "@material-ui/core";
import { ProfessionExtraSelectors } from "./profession-extra-selectors";
import { useEffect, useMemo, useState } from "react";
import { useInitialValues } from "@consultants/consultants-search-new/hooks/use-initial-values";
import { Search } from "@material-ui/icons";
import { Option } from "../utils";
import { useGlobalStore } from "@hooks";
import { professions } from "@bonliva-crm/common";

type Props = {
  initialValues: any;
  open: boolean;
  setOpen: (open: boolean) => void;
  onClear: () => void;
  onSubmit: (values: any) => void;
};

export const ProfessionFilterModal = ({
  initialValues,
  open,
  setOpen,
  onClear,
  onSubmit
}: Props) => {
  const [values, setValues] = useState(initialValues);
  const [professionSearchQuery, setProfessionSearchQuery] = useState("");
  const classes = useStyles({
    open
  });

  const { initialValues: formInitialValues } = useInitialValues();

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const handleClear = () => {
    onClear();
    setOpen(false);
  };
  const handleSave = () => {
    onSubmit(values);
    setOpen(false);
  };

  //facepalm
  const handleProfessionChange = (v: string[]) => {
    setValues({
      ...formInitialValues.profession,
      professions: v
    });
  };

  const store = useGlobalStore();
  const optionsProfessions = useMemo(() => {
    return [
      ...store.userService
        .getUserProfessions()
        .sort()
        .toLabelValue((x) => professions.getProfessionDisplayName(x)),
      { label: "Saknar yrke", value: "None" }
    ] as Option[];
  }, [store]);

  const filteredProfessions = useMemo(() => {
    return optionsProfessions.filter((option) =>
      option.label.toLowerCase().includes(professionSearchQuery.toLowerCase())
    );
  }, [optionsProfessions, professionSearchQuery]);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth onClose={() => setOpen(false)}>
      <DialogTitle>{locale.Profession}</DialogTitle>
      <DialogContent style={{ height: "100vh" }}>
        <div className={classes.root}>
          {/* Left side */}
          <div className={classes.content}>
            <div className={classes.stickyInput}>
              <TextField
                hiddenLabel
                placeholder="Sök"
                variant="outlined"
                fullWidth
                size="small"
                InputProps={{ startAdornment: <Search /> }}
                value={professionSearchQuery}
                onChange={(e) => setProfessionSearchQuery(e.target.value)}
              />
            </div>
            <CheckboxGroup
              value={values.professions}
              onChange={(_, selectedProfessions) =>
                handleProfessionChange(selectedProfessions)
              }
              options={filteredProfessions}
            />
          </div>

          {/* Divider */}
          <Divider flexItem orientation="vertical" />

          {/* Right side */}
          <div className={classes.rightContent}>
            {values?.professions?.length === 1 ? (
              <ProfessionExtraSelectors onChange={setValues} value={values} />
            ) : (
              <Typography variant="body1" align="center" color="textSecondary">
                {locale.SelectOneProfessionFilter}
              </Typography>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>{locale.Cancel}</Button>
        <Button onClick={handleClear}>{locale.Clear}</Button>
        <Button color="primary" onClick={handleSave}>
          {locale.OK}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "row",
    width: "100%",
    boxSizing: "border-box",
    gap: theme.spacing(1),
    height: "100%"
  },
  content: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    backgroundColor: Color.White,
    gap: theme.spacing(1),
    overflowY: "auto",
    maxHeight: "100vh",
    "& > :last-child": {
      padding: theme.spacing(1)
    }
  },
  stickyInput: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: Color.White,
    paddingBottom: theme.spacing(1),
    width: "100%",
    paddingRight: theme.spacing(1)
  },
  rightContent: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
    backgroundColor: Color.White,
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    paddingRight: "14px",
    paddingTop: "14px",
    overflowY: "auto",
    maxHeight: "100vh",
    overflow: "hidden"
  }
}));
